import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { CUSTOMER_PHONE_LABELS } from "@shared/customerPhoneIdentity";
import { useState } from "react";
import { toast } from "sonner";

export function CustomerPhoneNumbersSection({
  customerId,
  canManage = false,
}: {
  customerId: number;
  canManage?: boolean;
}) {
  const utils = trpc.useUtils();
  const phones = trpc.customers.listPhoneNumbers.useQuery({ customerId });
  const addMut = trpc.customers.addPhoneNumber.useMutation({
    onSuccess: () => {
      toast.success("Number added");
      utils.customers.listPhoneNumbers.invalidate({ customerId });
      utils.customers.getById.invalidate({ id: customerId });
      setOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.customers.updatePhoneNumber.useMutation({
    onSuccess: () => {
      utils.customers.listPhoneNumbers.invalidate({ customerId });
      utils.customers.getById.invalidate({ id: customerId });
    },
    onError: (e) => toast.error(e.message),
  });
  const removeMut = trpc.customers.removePhoneNumber.useMutation({
    onSuccess: () => {
      toast.success("Number removed");
      utils.customers.listPhoneNumbers.invalidate({ customerId });
      utils.customers.getById.invalidate({ id: customerId });
    },
    onError: (e) => toast.error(e.message),
  });

  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [label, setLabel] = useState<(typeof CUSTOMER_PHONE_LABELS)[number]>("Mobile");
  const [primary, setPrimary] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Phone numbers</h3>
        {canManage ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setPhone("");
              setLabel("Mobile");
              setPrimary(false);
              setOpen(true);
            }}
          >
            Add contact number
          </Button>
        ) : null}
      </div>
      <div className="space-y-2">
        {(phones.data ?? []).map((row) => (
          <div key={row.id} className="rounded-md border px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium">
                  {row.phone}
                  {row.isPrimary ? <span className="ml-2 text-xs text-emerald-700">Primary</span> : null}
                </p>
                <p className="text-xs text-muted-foreground">{row.label}</p>
              </div>
              {canManage ? (
              <div className="flex flex-wrap gap-1">
                {!row.isPrimary ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => updateMut.mutate({ customerId, phoneId: row.id, makePrimary: true })}
                  >
                    Make primary
                  </Button>
                ) : null}
                {!row.isPrimary ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removeMut.mutate({ customerId, phoneId: row.id })}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
              ) : null}
            </div>
          </div>
        ))}
        {(phones.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No stored numbers yet.</p>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add contact number</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Phone number</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>Label</Label>
              <select
                className="w-full rounded border px-2 py-2 text-sm"
                value={label}
                onChange={(e) => setLabel(e.target.value as (typeof CUSTOMER_PHONE_LABELS)[number])}
              >
                {CUSTOMER_PHONE_LABELS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={primary} onChange={(e) => setPrimary(e.target.checked)} />
              Make primary
            </label>
            <Button
              type="button"
              disabled={!phone.trim() || addMut.isPending}
              onClick={() =>
                addMut.mutate({
                  customerId,
                  phone: phone.trim(),
                  label,
                  makePrimary: primary,
                })
              }
            >
              Save number
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
